import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser'
import SoundAssetKeys from '../SoundAssetKeys';
import UIAssetKeys from '../UIAssetKeys';
import Cup, { ANIMATION_FINISHED } from './Cup';
import { getLetterArray } from './StackLetterGenerator';
import StackPlaceHolder from './StackPlaceHolder';

export default class CupStack extends Phaser.GameObjects.Container
{
    /**
     * 
     * @param {Phaser.Scene} scene 
     * @param {number} x 
     * @param {number} y 
     * @param {string[]} letters
     * @param {Phaser.GameObjects.Container} pRef
     */
    constructor(scene, x, y, letters, pRef, stackColor)
    {
        super(scene, x, y);

        this.parentRef = pRef;
        this.stackColor = stackColor;

        let sizerCup = ABCyaPhaser.Images.addImage(0, 0, UIAssetKeys._game_items_cup_red_0, this).setOrigin(0);
        this.stackItemWidth = sizerCup.width;
        this.stackItemHeight = sizerCup.height;

        this.stackLetters = getLetterArray(letters);
        this.readyPositions = [];
        this.flipPositions = [];
        this.stackOrUnstack = "stack";
        this.stackComplete = false;
        this.stackReady = false;
    }

    shuffleStackPositions()
    {
        let allLetters = [];
        for (let i = 0; i < this.stackLetters.length; i++)
        {
            for (let r = 0; r < this.stackLetters[i].length; r++)
            {
                allLetters.push(this.stackLetters[i][r]);
            }
        }

        this.stackLetters = getLetterArray(allLetters);
    }

    //this is called after the game level checks all of the stacks for bad words, creates the stack position object array that stores x,y letter values, etc
    initStack(xPos, yPos)
    {
        this.stackXPos = xPos;
        this.stackYPos = yPos;
        this.setStackPositions();
    }

    //set final stack position objects (for value, stacked state, etc) separate from letter generation so they can be checked with bad word filter
    setStackPositions()
    {
        /** @type {{_verticalIndex: number, _horizontalIndex: number, _positionId: any, _isFilled: boolean, _isReady: boolean, _cup: Cup, _outline: StackPlaceHolder}[][]} */
        this.stackPositions = [];
        while (this.stackPositions.length < this.stackLetters.length)
        {
            let stackLevel = [];
            while (stackLevel.length < this.stackLetters[this.stackPositions.length].length)
            {
                let positionObj = { _verticalIndex: this.stackPositions.length, _horizontalIndex: stackLevel.length, _positionId: this.stackLetters[this.stackPositions.length][stackLevel.length], _isFilled: false, _isReady: false, _cup: null };
                stackLevel.push(positionObj);
            }
            this.stackPositions.push(stackLevel);
        }

        this.showStackOutLine();
    }

    showStackOutLine()
    {
        for (let i = 0; i < this.stackPositions.length; i++)
        {
            //each level of stack, get start x pos for row and then line up outlines
            for (let r = 0; r < this.stackPositions[i].length; r++)
            {
                let stackItem = this.stackPositions[i][r];
                let outline = new StackPlaceHolder(this.scene, 0, 0, stackItem._positionId, this.stackColor);
                //in case outline needs to react
                stackItem._outline = outline;
                let outlinePos = this.getStackIndexXY(i, r);
                outline.scale = .8;
                outline.x = outlinePos.xData;
                outline.y = outlinePos.yData;
                outline.alpha = 0;
                if (this.parentRef instanceof Phaser.Scene)
                {
                    this.parentRef.add.existing(outline);
                }
                else
                {
                    this.parentRef.add(outline);
                }
            }
        }

        this.checkPositionsReady();
    }

    inputToStackPosition(val)
    {
        //remove from available letter until input complete
        //same weird issue as level class where I can't match the letter string to the index of the available letter array without looping like this, not sure what I'm missing;
        // SR note 1/11/2022: I think the issue the developer was having was the val is not a string but an array of string (or an array of an array of a string) so comparison fails
        // calling `toString` on the array condenses the whole thing down to a single character/string
        let stringVal = val.toString();
        let stringIndex = -1;

        for (let j = 0; j < this.readyPositions.length; j++)
        {
            let matchLetter = this.readyPositions[j];
            if (matchLetter == stringVal)
            {
                stringIndex = j;
                break;
            }
        }

        this.readyPositions.splice(stringIndex, 1);

        let stackPosition = this.getStackPositionIndex(val);
        let filled = this.getPositionFilledState(stackPosition[0], stackPosition[1]);

        //check if filled to stack or unstack
        if (filled == false)
        {
            if (this.stackPositions[stackPosition[0]][stackPosition[1]]._cup == null) this.fillStackPosition(stackPosition[0], stackPosition[1], val)
        }
        else
        {
            this.unfillStackPosition(stackPosition[0], stackPosition[1]);
        }
    }

    fillStackPosition(level, levelIndex, val)
    {
        let newCup = new Cup(this.scene, 0, 0, this.stackColor, val, this.parentRef);
        let outline = this.stackPositions[this.stackPositions.length > level + 1 ? level + 1 : level][0]._outline
        let cupXY = this.getStackIndexXY(level, levelIndex);
        if (this.parentRef instanceof Phaser.Scene)
        {
            this.parentRef.add.existing(newCup);
            this.parentRef.children.moveBelow(newCup, outline);
        }
        else
        {
            this.parentRef.add(newCup);
            this.parentRef.moveBelow(newCup, outline);
        }
        this.parentRef.cupsAdded.push(newCup);
        newCup.once(ANIMATION_FINISHED, () => ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._cup, this.scene));
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._whoosh, this.scene, 1, false, 0.1);

        //random drop or flip in
        let dropType = Phaser.Math.Between(0, 1);
        if (dropType == 0)
        {
            newCup.dropIn(cupXY.xData, cupXY.yData);
        }
        else
        {
            newCup.bounceIn(cupXY.xData, cupXY.yData);
        }

        this.stackPositions[level][levelIndex]._cup = newCup;
        this.stackPositions[level][levelIndex]._isFilled = true;
        this.checkPositionsReady();
    }

    unfillStackPosition(levelIndex, posIndex)
    {
        if (this.stackOrUnstack == "unstack")
        {
            const flipToDepth = this.parentRef?.list?.findIndex(go => go instanceof StackPlaceHolder) ?? 1
            this.stackPositions[levelIndex][posIndex]._cup.flipBack(flipToDepth);
            ABCyaPhaser.Tweens.addTween(this.stackPositions[levelIndex][posIndex]._cup.cupBase, 250, { tint: 0xdddddd }, Phaser.Math.Easing.Quadratic.Out);
            this.stackPositions[levelIndex][posIndex]._cup.once(ANIMATION_FINISHED, () => ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._cup, this.scene));
            this.stackPositions[levelIndex][posIndex]._cup.isDisposed = true;
            this.stackPositions[levelIndex][posIndex]._isFilled = false;
            this.checkPositionsReady();

            ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._whoosh, this.scene);
        }
        else
        {
            //flip up
            if (this.stackPositions[levelIndex][posIndex]._cup.animationReady == true)
            {
                ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._whoosh, this.scene);
                this.stackPositions[levelIndex][posIndex]._cup.flipUp();
                this.stackPositions[levelIndex][posIndex]._cup.once(ANIMATION_FINISHED, () => ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._cup, this.scene));
            }
        }
    }

    flipPosition(levelIndex, posIndex)
    {
        //flip up
        if (this.stackPositions[levelIndex][posIndex]._cup == null || this.stackPositions[levelIndex][posIndex]._cup.animationReady == false) return;

        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._whoosh, this.scene, 0.25);
        this.stackPositions[levelIndex][posIndex]._cup.flipUp();
        this.stackPositions[levelIndex][posIndex]._cup.once(ANIMATION_FINISHED, () => ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._cup, this.scene, 0.25));
    }

    getStackPositionIndex(val)
    {
        let levelIndex = -1;
        let positionIndex = -1;

        for (let i = 0; i < this.stackPositions.length; i++)
        {
            levelIndex = i;
            let level = this.stackPositions[i];
            for (let j = 0; j < level.length; j++)
            {
                if (level[j]._positionId == val)
                {
                    positionIndex = j;
                    break;
                }
            }

            if (positionIndex != -1) break;
        }

        return [levelIndex, positionIndex];
    }

    getStackIndexXY(levelIndex, pieceIndex)
    {
        //lower y position a little because of top space on cup bitmap
        let yPos = this.stackYPos + (((levelIndex + 1) * (this.stackItemHeight - 20)) * -1) + (this.stackItemHeight / 2);
        let startX = ((this.stackItemWidth / 2) * levelIndex) + (this.stackItemWidth / 2);
        let xPos = this.stackXPos + (startX + (pieceIndex * this.stackItemWidth));

        return { xData: xPos, yData: yPos };
    }

    getPositionReady(levelIndex, positionIndex)
    {
        let ready = false;
        //if positions are empty in level above then it is ready
        //if 0 check only same index
        //if end of row check only index - 1
        //else check same index and index  - 1
        let belowRightFilled = true;
        let belowLeftFilled = true;
        if (levelIndex > 0)
        {
            belowLeftFilled = this.getPositionFilledState(levelIndex - 1, positionIndex);
            belowRightFilled = this.getPositionFilledState(levelIndex - 1, positionIndex + 1);
        }
        let aboveRightFilled = false;
        let aboveLeftFilled = false;
        //
        if (levelIndex < this.stackPositions.length - 1 && positionIndex <= this.stackPositions[levelIndex + 1].length - 1)
        {
            aboveRightFilled = this.getPositionFilledState(levelIndex + 1, positionIndex);
        }

        if (levelIndex < this.stackPositions.length - 1 && positionIndex > 0)
        {
            aboveLeftFilled = this.getPositionFilledState(levelIndex + 1, positionIndex - 1);
        }

        if (aboveLeftFilled == false && aboveRightFilled == false && belowLeftFilled == true && belowRightFilled == true)
        {
            ready = true
        }
        else
        {
            ready = false;
        }

        return ready;
    }

    getPositionReady2(levelIndex, positionIndex)
    {
        let ready = false;
        if (this.getLevelReady(levelIndex) == false) return false;
        if (this.stackReady == false) return false;
        if (this.getPositionFilledState(levelIndex, positionIndex) == true && this.stackOrUnstack == "stack") return false;
        if (this.getPositionFilledState(levelIndex, positionIndex) == false && this.stackOrUnstack == "unstack") return false;

        let leftFilled = true;
        let rightFilled = false;
        if (positionIndex > 0)
        {
            leftFilled = this.getPositionFilledState(levelIndex, positionIndex - 1);
        }

        if (positionIndex < this.stackPositions[levelIndex].length - 1)
        {
            rightFilled = this.getPositionFilledState(levelIndex, positionIndex + 1);
        }

        if (leftFilled == true && rightFilled == false && this.stackOrUnstack == "stack") ready = true;
        if (leftFilled == false && (rightFilled == true || positionIndex == this.stackPositions[levelIndex].length - 1) && this.stackOrUnstack == "unstack") ready = true;
        if (this.stackOrUnstack == "unstack" && positionIndex == 0) ready = true;

        return ready;
    }

    getLevelReady(levelIndex)
    {
        let belowFilled = true;
        let aboveFilled = false;

        if (levelIndex > 0)
        {
            let subLevel = this.stackPositions[levelIndex - 1];
            for (let b = 0; b < subLevel.length; b++)
            {
                if (subLevel[b]._isFilled == false)
                {
                    belowFilled = false;
                    break;
                }
            }
        }

        if (levelIndex < this.stackPositions.length - 1)
        {
            let upLevel = this.stackPositions[levelIndex + 1];
            for (let u = 0; u < upLevel.length; u++)
            {
                if (upLevel[u]._isFilled == true)
                {
                    aboveFilled = true;
                    break;
                }
            }
        }

        let ready = (belowFilled == true && aboveFilled == false) ? true : false;

        return ready;
    }

    checkPositionsReady()
    {
        let totalPositions = 0;
        let totalFilled = 0;
        let totalUnFilled = 0;
        for (let i = 0; i < this.stackPositions.length; i++)
        {
            //check if level is free
            //hide or show outlines
            let levelReady = this.getLevelReady(i);

            for (let j = 0; j < this.stackPositions[i].length; j++)
            {
                let positionIsReady = this.getPositionReady2(i, j);

                this.stackPositions[i][j]._isReady = positionIsReady;
                this.stackPositions[i][j]._outline.showReadyInOutline(positionIsReady);
                this.stackPositions[i][j]._outline.alpha = (levelReady == false || this.stackOrUnstack == "unstack" || this.stackReady == false || this.stackPositions[i][j]._isFilled == true) ? 0 : 1;

                let cup = this.stackPositions[i][j]._cup;
                if (cup != null) cup.showTextReady(positionIsReady);

                if (positionIsReady == true)
                {
                    if (this.readyPositions.indexOf(this.stackPositions[i][j]._positionId) == -1) this.readyPositions.push(this.stackPositions[i][j]._positionId)
                }
                else
                {
                    if (this.readyPositions.indexOf(this.stackPositions[i][j]._positionId) != -1) this.readyPositions.splice(this.readyPositions.indexOf(this.stackPositions[i][j]._positionId), 1);
                    //add to a separate array for flipping already stacked cups following old rules
                    let flipIsReady = this.getPositionReady(i, j);
                    let flipString = this.stackPositions[i][j]._positionId.toString()
                    if (flipIsReady == true)
                    {
                        if (this.flipPositions.indexOf(flipString) == -1) this.flipPositions.push(flipString)
                    }
                    else
                    {
                        if (this.flipPositions.indexOf(flipString) != -1) this.flipPositions.splice(this.flipPositions.indexOf(flipString), 1);
                    }
                }

                if (this.stackPositions[i][j]._isFilled == true)
                {
                    totalFilled++;
                }
                else
                {
                    totalUnFilled++;
                }

                totalPositions++;
            }
        }

        //switch stacking and unstacking if all positions are filled/unfilled;
        if (this.stackOrUnstack == "stack" && totalFilled == totalPositions)
        {
            this.stackOrUnstack = "unstack";
            this.checkPositionsReady();
        }
        if (this.stackOrUnstack == "unstack" && totalUnFilled == totalPositions)
        {
            //end stack
            this.stackComplete = true;
            this.emit("STACK_COMPLETE", this);
        }
    }

    showAvailable()
    {
        for (let i = 0; i < this.readyPositions.length; i++)
        {
            let indexForReadyLetter = this.getStackPositionIndex(this.readyPositions[i]);
            let readyPositionObj = this.stackPositions[indexForReadyLetter[0]][indexForReadyLetter[1]];
            if (readyPositionObj._isFilled == true && readyPositionObj._cup != null)
            {
                ABCyaPhaser.Tweens.addTween(readyPositionObj._cup, 200, { angle: 30 }, Phaser.Math.Easing.Quadratic.Out, () =>
                {
                    ABCyaPhaser.Tweens.addTween(readyPositionObj._cup, 400, { angle: -30 }, Phaser.Math.Easing.Quadratic.InOut, () =>
                    {
                        ABCyaPhaser.Tweens.addTween(readyPositionObj._cup, 200, { angle: 0 }, Phaser.Math.Easing.Quadratic.In);
                    });
                });
            }
            ABCyaPhaser.Tweens.addTween(readyPositionObj._outline, 200, { angle: 30 }, Phaser.Math.Easing.Quadratic.Out, () =>
            {
                ABCyaPhaser.Tweens.addTween(readyPositionObj._outline, 400, { angle: -30 }, Phaser.Math.Easing.Quadratic.InOut, () =>
                {
                    ABCyaPhaser.Tweens.addTween(readyPositionObj._outline, 200, { angle: 0 }, Phaser.Math.Easing.Quadratic.In);
                });
            });
        }
    }

    getPositionFilledState(levelIndex, positionIndex)
    {
        return this.stackPositions[levelIndex][positionIndex]._isFilled;
    }

    getAvailableLetters()
    {
        return this.readyPositions;
    }

    getTotalStackWidth()
    {
        return this.stackItemWidth * this.stackLetters[0].length;
    }

    getTotalStackHeight()
    {
        return this.stackItemHeight * this.stackLetters.length;
    }

    resetStack()
    {
        for (let i = 0; i < this.stackPositions.length; i++)
        {
            for (let j = 0; j < this.stackPositions[i].length; j++)
            {
                let stackPos = this.stackPositions[i][j];
                stackPos._isFilled = false;
                stackPos._cup = null;
            }
        }
        this.stackOrUnstack = "stack";

        this.showStackOutLine()
    }

    hideStackOutline()
    {
        for (let i = 0; i < this.stackPositions.length; i++)
        {
            for (let j = 0; j < this.stackPositions[i].length; j++)
            {
                let stackPos = this.stackPositions[i][j];
                stackPos._outline.alpha = 0;
            }
        }
    }
}